import React from 'react';
import { Formik } from 'formik';
import { useLocation } from 'react-router';
import { useQueryString, useL10n } from '@uplift/hooks';

import { EMPTY_FUNCTION, EMPTY_STRING } from '@uplift/constants';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Alert, Button, PasswordAdornment, PasswordField, TextInput, UpliftThemeProvider } from '@uplift/ui-v2';
import { useSetPasswordValidationSchema } from './hooks/validation-schema.hook';
import { useSetPasswordMutation } from '@uplift/queries';

export type SetPassword = {
  onSubmit: typeof EMPTY_FUNCTION;
};


const useStyles = makeStyles()(theme => ({
  form: {
    width: '100%',
    maxWidth: '510px',
    textAlign: 'center',
  },
  text: {
    fontSize: '24px',
    lineHeight: '133.4%',
    maxWidth: '530px',
    textAlign: 'center',
    marginTop: 0,
    marginBottom: '34px',
    color: 'rgba(0,0,0,0.87)',
  },
  button: {
    width: '100%',
    maxWidth: '432px',
    marginTop: '25px',
  },
  passwordIcon: {
    color: theme.palette.text.secondary,
  }
}));

export const SetPasswordForm: React.FC<SetPassword> = ({
  onSubmit = EMPTY_FUNCTION,
}) => {
  const setPasswordMutation = useSetPasswordMutation();
  const location = useLocation();
  const params = useQueryString(location.search);
  const l10n = useL10n();
  const validationSchema = useSetPasswordValidationSchema(l10n);

  const initialValues = {
    password: EMPTY_STRING,
    confirmPassword: EMPTY_STRING,
  };

  const handleSubmit = React.useCallback(async ({ password }) => {
    await setPasswordMutation.mutateAsync({
      password,
      token: params?.token as string ?? '',
      isInviteToken: false,
    });

    onSubmit();
  }, [ onSubmit, params?.token, setPasswordMutation ]);

  const [ showPassword, setShowPassword ] = React.useState(false);

  const handleClick = React.useCallback(() => {
    setShowPassword(x => !x);
  }, []);

  const { classes } = useStyles();

  return (
    <UpliftThemeProvider>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {formik => (
          <form onSubmit={formik.handleSubmit} className={classes.form}>
            <p className={classes.text}>
              {l10n.auth.resetText}
            </p>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              {formik.submitCount > 0 && (!formik.isValid || setPasswordMutation.error) && (
                <Alert severity="error">
                  {setPasswordMutation.error.message || l10n.updatePassword.errors.pleaseCorrect}
                </Alert>
              )}
              <PasswordField name="password" />
              <TextInput
                name="confirmPassword"
                endAdornment={<PasswordAdornment handleClick={handleClick} showPassword={showPassword} className={classes.passwordIcon} />}
                fullWidth
                placeholder={l10n.auth.confirmPassword}
                type={showPassword ? 'text' : 'password'}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="legacyPrimary"
              size="large"
              className={classes.button}
              loading={formik.isSubmitting}
            >
              {l10n.auth.change}
            </Button>
          </form>
        )}
      </Formik>
    </UpliftThemeProvider>
  );
};
